import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import Button from '~/components/Button';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';

import AccountDropdown from './AccountDropdown';
import LogoHome from './LogoHome';
import Menu from './Menu';
import MobileSidePanel from './MobileSidePanel';

interface Props {
  publication: Publication;
  isLoggedIn?: boolean;
  subscription?: Subscription;
}

const ALLOWED_DIFF = 400;

const useNavMenuSizeCheck = () => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [shouldUseMobileMenu, setShouldUseMobileMenu] = useState(false);
  const [savedMenuWidth, setSavedMenuWidth] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  const diffCheck = (menuWidth: any) => {
    if (navRef.current) {
      const navWidth = navRef.current.offsetWidth;
      const diff = navWidth - menuWidth;

      if (diff < ALLOWED_DIFF) {
        !shouldUseMobileMenu && setShouldUseMobileMenu(true);
      } else {
        setShouldUseMobileMenu(false);
      }
    }
  };

  // Check state on initial render
  useEffect(() => {
    if (!isMounted && navRef.current && menuRef.current) {
      setSavedMenuWidth(menuRef.current.offsetWidth); // We want to save the width of the menu in state in case it is removed from the DOM
      diffCheck(menuRef.current.offsetWidth);
      setIsMounted(true);
    }
  }, [isMounted, navRef.current, menuRef.current]);

  // Listen for window resize
  useEffect(() => {
    const listener = () => {
      if (isMounted) {
        diffCheck(savedMenuWidth);
      }
    };

    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [isMounted, navRef.current]);

  return {
    shouldUseMobileMenu,
    menuRef,
    navRef,
    isMounted,
  };
};

const Nav = ({ publication, isLoggedIn, subscription }: Props) => {
  const { navRef, menuRef, isMounted, shouldUseMobileMenu } = useNavMenuSizeCheck();

  const isPremiumTierSubscriber = subscription?.tier === 'premium';
  const showSubscribeButton = !isLoggedIn && !publication.private;

  return (
    <nav ref={navRef} className="flex items-center justify-between bg-wt-tertiary px-4 py-2">
      <div className="mx-auto flex w-full max-w-6xl justify-between">
        <div className="flex items-center space-x-2">
          <LogoHome url={publication.logo.thumb.url} name={publication.name} />
          <div
            ref={menuRef}
            className={cx(
              !isMounted && 'opacity-0', // We want it to be transparent on initial render so that if we swap to the mobile menu there is no flash
              shouldUseMobileMenu ? 'hidden' : 'z-20 block',
            )}
          >
            <Menu publication={publication} />
          </div>
        </div>

        <div className="z-20 flex items-center">
          {showSubscribeButton && (
            <div className="mr-2">
              <Button variant="primary" linkProps={{ to: '/subscribe', prefetch: 'intent' }}>
                {publication.cta_text}
              </Button>
            </div>
          )}

          <div>
            {shouldUseMobileMenu ? (
              <MobileSidePanel publication={publication} isLoggedIn={isLoggedIn} subscription={subscription} />
            ) : (
              <AccountDropdown
                isLoggedIn={isLoggedIn}
                avatarUrl={subscription?.avatar_url}
                subscriberId={subscription?.subscriber_id}
                isPremiumEnabled={publication.premium_enabled}
                isReferralEnabled={publication.has_referral_program}
                isPremiumTierSubscriber={isPremiumTierSubscriber}
                publication={publication}
              />
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav;
